import React, { useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

import {
  selectAnalyticsStartDate,
  selectAnalyticsEndDate,
  selectProductCodes,
  selectDateRange,
  selectProductAnalyticsIds,
} from "../../store/product/ProductSelectors";
import { loadProductAnalytics } from "../../store/product/ProductActions";
import { selectOverlay } from "../../store/overlay/OverlaySelectors";
import { selectCurrentStoreId } from "../../store/store-list/StoreListSelectors";
import { OVERLAY_TABS } from "../../utils/Constants";
import {
  selectSortingRule,
  selectSortingRulesList,
  selectIsProductListFetching,
} from "../../store/product-list/ProductListSelectors";
import { BusinessRulesListDetails } from "../../store/product-list/ProductListTypes";
import { calculateStartEndDate } from "../../utils/ProductUtil";
import { selectSystemGeneratedRules } from "../../store/businessRules-list/BusinessRuleslistSelectors";
import { selectCurrentCategory } from "../../store/category/CategorySelectors";

const AnalyticsOverlayLabel = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const overlay = useSelector(selectOverlay);
  const startDate = useSelector(selectAnalyticsStartDate);
  const endDate = useSelector(selectAnalyticsEndDate);
  const productCodes = useSelector(selectProductCodes);
  const productAnalyticsIds = useSelector(selectProductAnalyticsIds);
  const storeId = useSelector(selectCurrentStoreId) || "";
  const selectedDateRange = useSelector(selectDateRange);
  const sortingRulesList = useSelector(selectSortingRulesList);
  const systemGeneratedRulesList = useSelector(selectSystemGeneratedRules);
  const allSortingRulesList = [
    ...systemGeneratedRulesList,
    ...sortingRulesList,
  ];

  const selectedRule = useSelector(selectSortingRule);
  const isProductListFetching = useSelector(selectIsProductListFetching);
  const currentCategory = useSelector(selectCurrentCategory);

  // Create stable keys for array comparison (compare content, not reference)
  const productAnalyticsIdsKey = useMemo(() => {
    return JSON.stringify([...productAnalyticsIds].sort());
  }, [productAnalyticsIds]);

  /**
   * Delta-fetch state:
   * - `fetchedIdsRef` — set of analytics IDs we've already requested for
   *   the active date range. Lets us dispatch only the **newly-arrived**
   *   IDs whenever the catalog streams in another cursor page (instead of
   *   redundantly re-fetching everything we already have).
   * - `fetchedRangeKeyRef` — `start|end` of the range whose IDs are in
   *   `fetchedIdsRef`. When the date range changes, we reset the set so
   *   the next dispatch covers the full current product list under the
   *   new range.
   * - `lastCategoryRef` — resets the set when the user navigates to a
   *   different category.
   */
  const fetchedIdsRef = useRef<Set<string>>(new Set());
  const fetchedRangeKeyRef = useRef<string>("");
  const lastCategoryRef = useRef<string | null>(null);

  // Reset when category changes
  useEffect(() => {
    if (lastCategoryRef.current !== currentCategory) {
      fetchedIdsRef.current = new Set();
      fetchedRangeKeyRef.current = "";
      lastCategoryRef.current = currentCategory;
    }
  }, [currentCategory]);

  useEffect(
    () => {
      if (
        overlay !== OVERLAY_TABS.ANALYTICS_OVERLAY ||
        isProductListFetching ||
        productCodes.length === 0
      ) {
        return;
      }

      // Resolve the requested date range — sorting-rule overrides win
      // over the user-selected date range in the picker.
      const sortingRule = allSortingRulesList?.find(
        (rule) => rule.ruleId === selectedRule,
      );
      let nextStartDate: string | undefined;
      let nextEndDate: string | undefined;

      if (sortingRule) {
        const range = calculateStartEndDate(
          sortingRule as BusinessRulesListDetails,
        );
        nextStartDate = range.startDate;
        nextEndDate = range.endDate;
      } else {
        nextStartDate = selectedDateRange.startDate;
        nextEndDate = selectedDateRange.endDate;
      }

      if (!nextStartDate || !nextEndDate) return;

      // If the requested range differs from what's currently in store
      // (per `selectAnalyticsStartDate/EndDate`), or differs from what
      // we've already pre-fetched on this client — clear the de-dup set
      // so the upcoming dispatch covers the full current ID list.
      const rangeKey = `${nextStartDate}|${nextEndDate}`;
      if (rangeKey !== fetchedRangeKeyRef.current) {
        fetchedIdsRef.current = new Set();
        fetchedRangeKeyRef.current = rangeKey;
      } else if (
        !sortingRule &&
        nextStartDate === startDate &&
        nextEndDate === endDate &&
        productAnalyticsIds.every((id) => fetchedIdsRef.current.has(id))
      ) {
        // Nothing new and the range is already loaded in the store.
        return;
      }

      // Compute the delta — only dispatch for IDs we haven't fetched yet
      // on this date range.
      const newIds = productAnalyticsIds.filter(
        (id) => !fetchedIdsRef.current.has(id),
      );
      if (newIds.length === 0) return;

      newIds.forEach((id) => fetchedIdsRef.current.add(id));

      dispatch(
        loadProductAnalytics({
          startDate: nextStartDate,
          endDate: nextEndDate,
          productCodes: newIds,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      productAnalyticsIdsKey,
      overlay,
      startDate,
      endDate,
      dispatch,
      storeId,
      selectedDateRange,
      selectedRule,
      allSortingRulesList,
      isProductListFetching,
    ],
  );

  return (
    <>
      {intl.formatMessage({
        id: "overlayTabs.analyticsLabel",
        defaultMessage: "Analytics",
      })}
    </>
  );
};

export default React.memo(AnalyticsOverlayLabel);
